import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';

export type ConnectionStatus = 'idle' | 'connecting' | 'online' | 'offline' | 'reconnecting' | 'error';

export interface ConnectionDebugInfo {
  socketUrl: string | null;
  socketPath: string | null;
  authUserId: string | null;
  authTokenPreview: string | null;
  engineProtocol: string | null;
  lastSocketEvent: string | null;
  lastSocketEventAt: string | null;
}

export interface ConnectionStateModel {
  status: ConnectionStatus;
  transport: 'websocket';
  reconnectAttemptCount: number;
  lastConnectedAt: string | null;
  lastDisconnectedAt: string | null;
  lastError: string | null;
  debug: ConnectionDebugInfo;
}

const CONNECTION_STATE_DEFAULTS: ConnectionStateModel = {
  status: 'idle',
  transport: 'websocket',
  reconnectAttemptCount: 0,
  lastConnectedAt: null,
  lastDisconnectedAt: null,
  lastError: null,
  debug: {
    socketUrl: null,
    socketPath: null,
    authUserId: null,
    authTokenPreview: null,
    engineProtocol: null,
    lastSocketEvent: null,
    lastSocketEventAt: null
  }
};

export class SetConnectionStatus {
  static readonly type = '[Connection] Set Status';

  constructor(
    public readonly payload: Pick<ConnectionStateModel, 'status'> & {
      lastError?: string | null;
    }
  ) {}
}

export class IncrementReconnectAttempt {
  static readonly type = '[Connection] Increment Reconnect Attempt';
}

export class UpdateConnectionDebug {
  static readonly type = '[Connection] Update Debug';

  constructor(public readonly payload: Partial<ConnectionDebugInfo>) {}
}

export class ResetConnectionState {
  static readonly type = '[Connection] Reset';
}

@State<ConnectionStateModel>({
  name: 'connection',
  defaults: CONNECTION_STATE_DEFAULTS
})
@Injectable()
export class ConnectionState {
  @Selector()
  static snapshot(state: ConnectionStateModel): ConnectionStateModel {
    return state;
  }

  @Selector()
  static status(state: ConnectionStateModel): ConnectionStatus {
    return state.status;
  }

  @Selector()
  static isOnline(state: ConnectionStateModel): boolean {
    return state.status === 'online';
  }

  @Selector()
  static debug(state: ConnectionStateModel): ConnectionDebugInfo {
    return state.debug;
  }

  @Action(SetConnectionStatus)
  setConnectionStatus(
    ctx: StateContext<ConnectionStateModel>,
    action: SetConnectionStatus
  ): void {
    const timestamp = new Date().toISOString();
    ctx.patchState({
      status: action.payload.status,
      lastError: action.payload.lastError ?? null,
      lastConnectedAt:
        action.payload.status === 'online' ? timestamp : ctx.getState().lastConnectedAt,
      lastDisconnectedAt:
        action.payload.status === 'offline' || action.payload.status === 'error'
          ? timestamp
          : ctx.getState().lastDisconnectedAt
    });
  }

  @Action(IncrementReconnectAttempt)
  incrementReconnectAttempt(ctx: StateContext<ConnectionStateModel>): void {
    const state = ctx.getState();
    ctx.patchState({
      reconnectAttemptCount: state.reconnectAttemptCount + 1,
      status: 'reconnecting'
    });
  }

  @Action(UpdateConnectionDebug)
  updateConnectionDebug(
    ctx: StateContext<ConnectionStateModel>,
    action: UpdateConnectionDebug
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      debug: {
        ...state.debug,
        ...action.payload,
        lastSocketEventAt:
          action.payload.lastSocketEvent !== undefined
            ? new Date().toISOString()
            : (action.payload.lastSocketEventAt ?? state.debug.lastSocketEventAt)
      }
    });
  }

  @Action(ResetConnectionState)
  resetConnectionState(ctx: StateContext<ConnectionStateModel>): void {
    ctx.setState(CONNECTION_STATE_DEFAULTS);
  }
}

@Injectable({ providedIn: 'root' })
export class ConnectionFacade {
  private readonly store = inject(Store);

  readonly connection = this.store.selectSignal(ConnectionState.snapshot);
  readonly status = this.store.selectSignal(ConnectionState.status);
  readonly isOnline = this.store.selectSignal(ConnectionState.isOnline);
  readonly debug = this.store.selectSignal(ConnectionState.debug);

  setStatus(status: ConnectionStatus, lastError?: string | null): void {
    this.store.dispatch(new SetConnectionStatus({ status, lastError }));
  }

  incrementReconnectAttempt(): void {
    this.store.dispatch(new IncrementReconnectAttempt());
  }

  updateDebug(payload: Partial<ConnectionDebugInfo>): void {
    this.store.dispatch(new UpdateConnectionDebug(payload));
  }

  reset(): void {
    this.store.dispatch(new ResetConnectionState());
  }
}
